import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ApiKnowledgeBase, { IKnowledgeBaseArticle } from "../api/knowledgeBase";

export function useArticles(parentId?: number | null) {
  return useQuery({
    queryKey: ["kb-articles", parentId],
    queryFn: () =>
      ApiKnowledgeBase.getArticles(parentId).then((res) => res.articles),
  });
}

export function useArticle(id: number | null) {
  return useQuery({
    queryKey: ["kb-article", id],
    queryFn: () =>
      id ? ApiKnowledgeBase.getArticle(id).then((res) => res.article) : null,
    enabled: id !== null,
    staleTime: 60 * 1000,
  });
}

export function useSearchArticles(
  searchTerm: string,
  enabled: boolean = true
) {
  const [debouncedTerm, setDebouncedTerm] = React.useState(searchTerm);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedTerm(searchTerm), 300);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  return useQuery({
    queryKey: ["kb-search", debouncedTerm],
    queryFn: () =>
      ApiKnowledgeBase.searchArticles(debouncedTerm).then((res) => res.articles),
    enabled: enabled && debouncedTerm.trim().length > 0,
    staleTime: 30 * 1000,
  });
}

export function useArticleMutations() {
  const queryClient = useQueryClient();

  const createArticle = useMutation({
    mutationFn: (data: {
      title: string;
      slug: string;
      content: any;
      parent_id?: number | null;
      position?: number;
    }) => ApiKnowledgeBase.createArticle(data).then((res) => res.article),
    onSuccess: () => {
      // Invalidate all article queries to refresh the tree
      queryClient.invalidateQueries({ queryKey: ["kb-articles"] });
    },
  });

  const updateArticle = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Partial<{
        title: string;
        slug: string;
        content: any;
        parent_id: number | null;
        position: number;
      }>;
    }) => ApiKnowledgeBase.updateArticle(id, data).then((res) => res.article),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["kb-articles"] });
      queryClient.invalidateQueries({ queryKey: ["kb-article", variables.id] });
    },
  });

  const deleteArticle = useMutation({
    mutationFn: (id: number) => ApiKnowledgeBase.deleteArticle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kb-articles"] });
    },
  });

  return {
    createArticle,
    updateArticle,
    deleteArticle,
  };
}

export function useTags() {
  return useQuery({
    queryKey: ["kb-tags"],
    queryFn: () => ApiKnowledgeBase.getTags().then((res) => res.tags),
  });
}

export const useArticlePositionUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      updates: Array<{ id: number; parent_id: number | null; position: number }>
    ) => ApiKnowledgeBase.updateArticlesBatch(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kb-articles"] });
    },
  });
};
